import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  Search, 
  Plus, 
  Filter, 
  Download,
  Eye,
  Edit,
  MoreHorizontal,
  Users,
  Calendar,
  MapPin,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Crown,
  Globe,
  Mail,
  Phone,
  ExternalLink,
  BarChart3,
  TrendingUp,
  DollarSign
} from 'lucide-react';

const UltraOrgs = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [total, setTotal] = useState(0);

  const API = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  useEffect(() => { fetchOrgs(); }, [search, statusFilter, typeFilter]);

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams({ limit: 100 });
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('org_type', typeFilter);
      const res = await axios.get(`${API}/api/ultra/organizations?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const items = (res.data.items || []).map(o => ({
        ...o,
        // Normalize fields for display
        status: (o.status || 'active').toLowerCase(),
        org_type: o.org_type || 'UNKNOWN',
        city: (o.addresses || [])[0]?.city || '',
        user_count: o.user_count || 0,
        subscription: o.subscription || { plan_code: 'dynamic', status: 'active', mrr: 0 },
        contact: o.primary_contact || { email: '', phone: '' },
        last_login: o.last_activity || o.updated_at || o.created_at,
        total_revenue: o.total_revenue || 0,
        projects_count: o.projects_count || 0,
        rating: o.rating || null,
      }));
      setOrgs(items);
      setTotal(res.data.total || items.length);
    } catch (error) {
      console.error('Orgs fetch error:', error);
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filter/sort (since API filters by search, additional client filters)
  const filteredOrgs = orgs.filter(org => {
    const matchesStatus = statusFilter === 'all' || org.status === statusFilter;
    const matchesType = typeFilter === 'all' || org.org_type === typeFilter;
    return matchesStatus && matchesType;
  });

  const sortedOrgs = [...filteredOrgs].sort((a, b) => {
    switch (sortBy) {
      case 'name_asc': return (a.display_name||'').localeCompare(b.display_name||'');
      case 'name_desc': return (b.display_name||'').localeCompare(a.display_name||'');
      case 'created_asc': return new Date(a.created_at) - new Date(b.created_at);
      case 'created_desc': return new Date(b.created_at) - new Date(a.created_at);
      case 'revenue_desc': return b.total_revenue - a.total_revenue;
      case 'users_desc': return b.user_count - a.user_count;
      default: return 0;
    }
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', variant: 'default', icon: CheckCircle2, color: 'text-green-600' },
      trial: { label: 'Deneme', variant: 'secondary', icon: Clock, color: 'text-yellow-600' },
      suspended: { label: 'Askıda', variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      pending: { label: 'Beklemede', variant: 'outline', icon: AlertCircle, color: 'text-blue-600' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const exportCSV = () => {
    const headers = ['Org ID', 'Adı', 'Tür', 'Durum', 'Şehir', 'Kullanıcı Sayısı', 'Plan', 'Toplam Gelir', 'Oluşturulma'];
    const rows = sortedOrgs.map(o => [
      o.id || o.org_id || '',
      o.display_name || '',
      o.org_type || '',
      o.status || '',
      o.city || '',
      o.user_count || 0,
      o.subscription?.plan_code || '',
      o.total_revenue || 0,
      o.created_at ? new Date(o.created_at).toLocaleDateString('tr-TR') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `organizasyonlar_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8 bg-gray-50 -m-3 sm:-m-6 p-3 sm:p-6 min-h-screen">
      {/* Enhanced Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Organizasyon Yönetimi
            </h1>
            <p className="text-gray-600 mt-1">
              Sistem genelindeki {orgs.length} organizasyon ve detaylı analitikleri
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="table">Tablo Görünümü</option>
              <option value="grid">Kart Görünümü</option>
            </select>
            
            <Button variant="outline" size="sm" className="gap-2" onClick={exportCSV} data-testid="export-orgs-csv-btn">
              <Download className="h-4 w-4" />
              Excel İndir
            </Button>
            <Button size="sm" className="gap-2 bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4" />
              Yeni Organizasyon
            </Button>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      <Card className="shadow-sm border-0">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Organizasyon, email veya şirket adı ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="trial">Deneme</option>
              <option value="suspended">Askıda</option>
              <option value="pending">Beklemede</option>
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
            >
              <option value="all">Tüm Tipler</option>
              <option value="CONTRACTOR">Contractor</option>
              <option value="EXHIBITOR">Exhibitor</option>
              <option value="SUPPLIER">Supplier</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
            >
              <option value="created_desc">Yeni Kayıtlar</option>
              <option value="created_asc">Eski Kayıtlar</option>
              <option value="name_asc">A-Z Sıralama</option>
              <option value="name_desc">Z-A Sıralama</option>
              <option value="revenue_desc">Gelire Göre</option>
              <option value="users_desc">Kullanıcı Sayısına Göre</option>
            </select>
          </div>
          
          {search && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                "{search}" için {filteredOrgs.length} sonuç bulundu
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearch('')}
                className="text-gray-500 hover:text-gray-700"
              >
                Temizle
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Organizations Display */}
      {loading ? (
        <div className="grid grid-cols-1 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="flex space-x-4">
                  <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-sm border-0">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 rounded-t-xl">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <span className="text-lg">Organizasyonlar</span>
                  <p className="text-sm text-gray-500 mt-1 font-normal">
                    {filteredOrgs.length} organizasyon listeleniyor
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {orgs.some(o => o.total_revenue > 0) && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Toplam Gelir: {formatCurrency(orgs.reduce((sum, org) => sum + org.total_revenue, 0))}
                  </Badge>
                )}
                {orgs.some(o => (o.subscription?.mrr || 0) > 0) && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    Aktif MRR: {formatCurrency(orgs.reduce((sum, org) => sum + (org.subscription?.mrr || 0), 0))}
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Organizasyon</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Tip & Plan</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Konum</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">İletişim</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Performans</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Son Aktivite</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Durum</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedOrgs.map((org) => (
                    <tr key={org.id} className="hover:bg-blue-50/30 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm ${
                            org.org_type === 'CONTRACTOR' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                            org.org_type === 'EXHIBITOR' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                            'bg-gradient-to-br from-orange-500 to-orange-600'
                          }`}>
                            {org.display_name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{org.display_name}</div>
                            <div className="text-sm text-gray-500">{org.legal_name}</div>
                            <div className="text-xs text-gray-400 flex items-center mt-1">
                              <Users className="h-3 w-3 mr-1" />
                              {org.user_count} kullanıcı
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-2">
                          <Badge 
                            variant="outline" 
                            className={`font-medium ${
                              org.org_type === 'CONTRACTOR' ? 'text-blue-700 border-blue-200 bg-blue-50' :
                              org.org_type === 'EXHIBITOR' ? 'text-green-700 border-green-200 bg-green-50' :
                              'text-orange-700 border-orange-200 bg-orange-50'
                            }`}
                          >
                            {org.org_type}
                          </Badge>
                          <div className="flex items-center space-x-1">
                            <span className="capitalize font-medium text-gray-700 text-sm">{org.subscription.plan_code}</span>
                            {org.subscription.plan_code === 'enterprise' && <Crown className="h-3 w-3 text-yellow-500" />}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-1 text-gray-700">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span>{org.city}, {org.country}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-1 text-sm text-gray-600">
                            <Mail className="h-3 w-3" />
                            <span className="truncate max-w-[150px]">{org.contact.email}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-sm text-gray-600">
                            <Phone className="h-3 w-3" />
                            <span>{org.contact.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <BarChart3 className="h-4 w-4 text-green-600" />
                            <span className="font-semibold text-gray-900">{formatCurrency(org.total_revenue)}</span>
                          </div>
                          <div className="text-xs text-gray-500">{org.projects_count} proje • ★ {org.rating}</div>
                          {org.subscription.mrr > 0 && (
                            <div className="text-xs text-blue-600 font-medium">MRR: {formatCurrency(org.subscription.mrr)}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-700">
                          {org.last_login && !isNaN(new Date(org.last_login))
                            ? new Date(org.last_login).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
                            : '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {org.last_login && !isNaN(new Date(org.last_login))
                            ? `${Math.floor((new Date() - new Date(org.last_login)) / (1000 * 60 * 60 * 24))} gün önce`
                            : ''}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {getStatusBadge(org.status)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-blue-100">
                            <Eye className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-blue-100">
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-100">
                            <MoreHorizontal className="h-4 w-4 text-gray-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
              <div className="text-sm text-gray-500">
                Toplam {orgs.length} organizasyondan {filteredOrgs.length} tanesi gösteriliyor
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" disabled>
                  Önceki
                </Button>
                <Button variant="outline" size="sm" className="bg-blue-600 text-white border-blue-600">
                  1
                </Button>
                <Button variant="outline" size="sm">
                  Sonraki
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UltraOrgs;